/**
 * MenuTab - Admin tab for managing navigation menu items
 * Supports drag-and-drop reordering and event linking
 * 
 * Refactored: Extracted SortableMenuItem and MenuItemForm to /menu/
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, GripVertical } from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableMenuItem, MenuItemForm, PREDEFINED_PAGES } from "./menu";

export const MenuTab = () => {
  const [items, setItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ 
    name: "", href: "", is_external: false, is_visible: true,
    item_type: "link", event_id: "", event_logo_url: "",
    event_dates: "", display_mode: "name"
  });
  const [selectedPage, setSelectedPage] = useState("");
  const [selectedEvent, setSelectedEvent] = useState("");
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const fetchItems = async () => {
    try {
      const r = await axios.get(`${API}/settings/menu`);
      setItems((r.data || []).sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (error) { console.error("Error fetching menu:", error); }
  };

  const fetchEvents = async () => {
    try {
      const r = await axios.get(`${API}/events?include_unpublished=false`);
      setEvents(r.data || []);
    } catch (error) { console.error("Error fetching events:", error); }
  };

  useEffect(() => { fetchItems(); fetchEvents(); }, []);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      const newItems = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);
      try {
        const updates = newItems.map((item, index) => ({ id: item.id, order: index + 1 }));
        await axios.put(`${API}/settings/menu/reorder`, { items: updates }, { headers: getAuthHeader() });
      } catch (error) { console.error("Error saving order:", error); fetchItems(); }
    }
  };

  const handlePageSelect = (pageName) => {
    const page = PREDEFINED_PAGES.find(p => p.name === pageName);
    if (page) {
      setSelectedPage(pageName);
      setSelectedEvent("");
      if (page.type !== "custom") {
        setFormData({ ...formData, name: page.name, href: page.href, is_external: false, item_type: "link", event_id: "" });
      } else {
        setFormData({ ...formData, name: "", href: "", is_external: false, item_type: "link", event_id: "" });
      }
    }
  };

  const handleEventSelect = (eventId) => {
    const event = events.find(e => e.id === eventId);
    if (event) {
      setSelectedEvent(eventId);
      setSelectedPage("");
      let dateDisplay = "";
      if (event.start_date) {
        const start = new Date(event.start_date + "T00:00:00");
        dateDisplay = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (event.end_date && event.end_date !== event.start_date) {
          const end = new Date(event.end_date + "T00:00:00");
          dateDisplay += ` - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
        }
      }
      setFormData({ 
        ...formData, name: event.name, href: `/events/${event.slug}`,
        is_external: false, item_type: "event", event_id: event.id,
        event_logo_url: event.logo_url || "", event_dates: dateDisplay, display_mode: "name"
      });
    }
  };

  const resetForm = () => {
    setFormData({ 
      name: "", href: "", is_external: false, is_visible: true,
      item_type: "link", event_id: "", event_logo_url: "",
      event_dates: "", display_mode: "name"
    });
    setSelectedPage("");
    setSelectedEvent("");
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/settings/menu`, { 
        ...formData, order: items.length + 1, is_visible: formData.is_visible !== false 
      }, { headers: getAuthHeader() });
      await fetchItems();
      setShowForm(false);
      resetForm();
      window.dispatchEvent(new Event("menuUpdated"));
    } catch (error) { console.error("Error creating:", error); }
    setSaving(false);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/settings/menu/${editingId}`, formData, { headers: getAuthHeader() });
      await fetchItems();
      setEditingId(null);
      resetForm();
      window.dispatchEvent(new Event("menuUpdated"));
    } catch (error) { console.error("Error updating:", error); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    const item = items.find(i => i.id === id);
    if (item && (item.href === "/guide" || item.is_protected)) {
      alert("This menu item is protected and cannot be deleted. You can hide it instead.");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this menu item?")) return;
    try {
      await axios.delete(`${API}/settings/menu/${id}`, { headers: getAuthHeader() });
      setItems(items.filter(i => i.id !== id));
      window.dispatchEvent(new Event("menuUpdated"));
    } catch (error) { console.error("Error deleting:", error); }
  };

  const handleToggleVisibility = async (item) => {
    try {
      await axios.put(`${API}/settings/menu/${item.id}`, { ...item, is_visible: !item.is_visible }, { headers: getAuthHeader() });
      await fetchItems();
      window.dispatchEvent(new Event("menuUpdated"));
    } catch (error) { console.error("Error toggling:", error); }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      name: item.name, href: item.href, order: item.order || 1,
      is_external: item.is_external || false, is_visible: item.is_visible !== false,
      item_type: item.item_type || "link", event_id: item.event_id || "",
      event_logo_url: item.event_logo_url || "", event_dates: item.event_dates || "",
      display_mode: item.display_mode || "name"
    });
    setSelectedPage("Custom Link");
    setSelectedEvent("");
    setShowForm(false);
  };

  const cancelEdit = () => { setEditingId(null); resetForm(); };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-secondary text-2xl font-bold uppercase tracking-tight text-white">Menu Manager</h2>
          <p className="text-zinc-500 text-sm mt-1">Add, edit, or reorder navigation menu items</p>
        </div>
        <Button 
          onClick={() => { setShowForm(!showForm); setEditingId(null); resetForm(); }} 
          className="bg-white text-black hover:bg-zinc-200 text-xs px-4 py-2 h-auto rounded-none"
        >
          <Plus className="w-4 h-4 mr-2" />Add Menu Item
        </Button>
      </div>

      {/* Drag Hint */}
      <div className="flex items-center gap-2 text-sm text-orange-500 bg-orange-500/10 border border-orange-500/30 px-4 py-2">
        <GripVertical className="w-4 h-4" />
        <span>Drag items to reorder them in the navigation menu</span>
      </div>

      {/* Add New Form */}
      {showForm && (
        <MenuItemForm
          formData={formData}
          setFormData={setFormData}
          selectedPage={selectedPage}
          setSelectedPage={setSelectedPage}
          selectedEvent={selectedEvent}
          setSelectedEvent={setSelectedEvent}
          events={events}
          handlePageSelect={handlePageSelect}
          handleEventSelect={handleEventSelect}
          handleCreate={handleCreate}
          onCancel={() => { setShowForm(false); resetForm(); }}
          saving={saving}
        />
      )}

      {/* Menu Items List with Drag and Drop */}
      <div className="space-y-2">
        {items.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            <p>No menu items yet. Add your first menu item above.</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map(item => item.id)} strategy={verticalListSortingStrategy}>
              {items.map((item) => (
                <SortableMenuItem
                  key={item.id}
                  item={item}
                  editingId={editingId}
                  formData={formData}
                  setFormData={setFormData}
                  saving={saving}
                  handleUpdate={handleUpdate}
                  handleDelete={handleDelete}
                  handleToggleVisibility={handleToggleVisibility}
                  startEdit={startEdit}
                  cancelEdit={cancelEdit}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Help Text */}
      <div className="p-4 bg-zinc-900/30 border border-white/5 text-sm text-zinc-500">
        <p className="mb-2"><strong className="text-zinc-400">Tips:</strong></p>
        <ul className="list-disc list-inside space-y-1">
          <li><GripVertical className="w-3 h-3 inline text-orange-500" /> Drag the handle to reorder menu items</li>
          <li>Use the eye icon to show/hide menu items without deleting them</li>
          <li>External links will open in a new browser tab</li>
          <li>Maximum 8 items will be displayed in the navigation bar (4 on each side of the logo)</li>
        </ul>
      </div>
    </motion.div>
  );
};

export default MenuTab;
